/**
 * LLMControls component - Audio toggle switch and LLM button
 * Factory function following the app's architectural patterns
 */

import { createIconButton, type IconButtonAPI } from "@UI/IconButton/index.js";
import { getGlobalTTS, TTS_TEST_MESSAGES } from "@utils/tts-api.ts";

export interface LLMControlsConfig {
  readonly onAudioToggle?: (isMuted: boolean) => void;
  readonly onLLMClick?: () => void;
  readonly initialMuted?: boolean;
}

export interface LLMControlsAPI {
  readonly getElement: () => HTMLElement;
  readonly setMuted: (muted: boolean) => void;
  readonly getMuted: () => boolean;
  readonly setLLMDisabled: (disabled: boolean) => void;
  readonly applyVisibility: (visibility: {
    audio: boolean;
    llmButton: boolean;
  }) => void;
  readonly destroy: () => void;
}

/**
 * Creates LLMControls component with audio toggle and LLM button
 * @param config - Configuration for the LLM controls
 * @returns LLMControls API
 */
export const createLLMControls = (
  config: LLMControlsConfig = {}
): LLMControlsAPI => {
  const { onAudioToggle, onLLMClick, initialMuted = false } = config;

  let isDestroyed = false;
  let isMuted = initialMuted;
  let audioButtonAPI: IconButtonAPI | null = null;
  let llmButtonAPI: IconButtonAPI | null = null;

  // Get global TTS instance
  const tts = getGlobalTTS();

  // Create container element
  const container = document.createElement("div");
  container.className = "llm-controls";

  /**
   * Initialize the LLM controls with two IconButtons
   */
  const initLLMControls = (): void => {
    try {
      // Create audio toggle button (switch behavior)
      // Important: pass stable off-state icon; selected state controls icon swap
      audioButtonAPI = createIconButton({
        icon: "interactions_audio",
        size: 44,
        iconSize: 28,
        behavior: "switch",
        switchIcon: "interactions_mute",
        selected: isMuted,
        className: "llm-button llm-button--audio",
        onClick: (selected?: boolean) => {
          if (isDestroyed || !audioButtonAPI) return;

          // Use selected state coming from IconButton for truth
          isMuted = !!selected;

          // Test TTS functionality when unmuting
          if (!isMuted) {
            // Cycle through different test messages with intonations
            const messages = Object.values(TTS_TEST_MESSAGES);
            const randomIndex = Math.floor(Math.random() * messages.length);
            const randomMessage = messages[randomIndex];
            if (randomMessage) {
              console.log("🔊 TTS: Playing test message:", randomMessage.text);
              console.log(
                "🎵 TTS: Intonation settings:",
                randomMessage.intonation
              );
              tts.speak(randomMessage);
            }
          } else {
            console.log("🔇 TTS: Stopping speech");
            // Stop any current speech when muting
            tts.stop();
          }

          // Call callback
          onAudioToggle?.(isMuted);
        },
      });

      // Create LLM button (simple button)
      llmButtonAPI = createIconButton({
        icon: "llm_llm",
        size: 44,
        iconSize: 28,
        behavior: "button",
        className: "llm-button llm-button--llm",
        onClick: () => onLLMClick?.(),
      });

      // Append buttons to container
      container.appendChild(audioButtonAPI.getElement());
      container.appendChild(llmButtonAPI.getElement());
    } catch (error) {
      console.error("Failed to create LLMControls component:", error);
      throw error;
    }
  };

  /**
   * Set muted state
   */
  const setMuted = (muted: boolean): void => {
    if (isDestroyed || !audioButtonAPI) return;

    isMuted = muted;
    audioButtonAPI.setSelected(muted);
  };

  /**
   * Get current muted state
   */
  const getMuted = (): boolean => {
    return isMuted;
  };

  /**
   * Set LLM button disabled state
   */
  const setLLMDisabled = (disabled: boolean): void => {
    if (isDestroyed || !llmButtonAPI) return;
    llmButtonAPI.setDisabled(disabled);
  };

  const applyVisibility = (visibility: {
    audio: boolean;
    llmButton: boolean;
  }): void => {
    if (isDestroyed) return;

    const showAudio = visibility.audio !== false;
    const showLLM = visibility.llmButton !== false;

    if (audioButtonAPI) {
      audioButtonAPI
        .getElement()
        .style.setProperty("display", showAudio ? "" : "none");
    }
    if (llmButtonAPI) {
      llmButtonAPI
        .getElement()
        .style.setProperty("display", showLLM ? "" : "none");
    }

    container.style.display = showAudio || showLLM ? "" : "none";
  };

  /**
   * Clean up resources
   */
  const destroy = (): void => {
    if (isDestroyed) return;

    isDestroyed = true;

    // Stop any current TTS speech
    tts.stop();

    // Destroy button components
    audioButtonAPI?.destroy();
    llmButtonAPI?.destroy();

    // Clear references
    audioButtonAPI = null;
    llmButtonAPI = null;

    // Remove from DOM if still connected
    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }
  };

  // Initialize the component
  initLLMControls();

  return {
    getElement: () => container,
    setMuted,
    getMuted,
    setLLMDisabled,
    applyVisibility,
    destroy,
  };
};

/**
 * Creates LLMControls component and appends to container
 * @param container - Container element to append to
 * @param config - Configuration for the LLM controls
 * @returns LLMControls API
 */
export const createLLMControlsInContainer = (
  container: HTMLElement,
  config: LLMControlsConfig = {}
): LLMControlsAPI => {
  const llmControls = createLLMControls(config);
  container.appendChild(llmControls.getElement());
  return llmControls;
};
